import type { ReactNode } from 'react';

import { SHORTCUTS } from '../keyboard.js';

/**
 * The small overlays: the shortcut list and the onboarding question.
 *
 * Adding a feed used to live here too and moved to `add-feed.tsx` when it
 * grew a second step (M2.6.2) — a dialog with a discovery round trip and a
 * candidate choice is no longer a small overlay.
 */

/** M1.17.1's fourth criterion: the shortcuts are documented in the app. */
export function ShortcutHelp({ onClose }: { onClose: () => void }): ReactNode {
  return (
    <div
      className="overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
    >
      <div className="overlay__card">
        <h2 className="overlay__title">Keyboard shortcuts</h2>
        <dl className="shortcuts">
          {SHORTCUTS.map((shortcut) => (
            <div className="shortcuts__row" key={shortcut.keys}>
              <dt className="shortcuts__keys">{shortcut.keys}</dt>
              <dd className="shortcuts__description">{shortcut.description}</dd>
            </div>
          ))}
        </dl>
        <button className="button" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

/**
 * The single onboarding question (M1.18.1, [D18](../../../Design-Decisions.md)).
 *
 * **One question, asked once, not a settings page.** D18's risk runs both
 * ways: a precise red badge on first launch contradicts the positioning within
 * five seconds, and an Inbox mode nobody can find makes Maya conclude the
 * product cannot count. Asking plainly is what serves both — and the answer is
 * recorded, because it is the only evidence H8 will ever get.
 *
 * Quiet is pre-selected and the dialog can be dismissed, which means the
 * default holds for anybody who does not care.
 */
export function InboxModeQuestion({
  onChoose,
}: {
  onChoose: (inboxMode: boolean) => void;
}): ReactNode {
  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label="Unread counts">
      <div className="overlay__card">
        <h2 className="overlay__title">Do you want unread counts?</h2>
        <p className="overlay__text">
          By default this reader is quiet: no numbers, no badges. Nothing is waiting for
          you.
        </p>
        <p className="overlay__text">
          If you would rather see exactly how much is unread — per feed, per folder and in
          total — Inbox mode turns the numbers on. You can change this later.
        </p>
        <div className="overlay__actions">
          <button
            className="button button--primary"
            onClick={() => {
              onChoose(false);
            }}
          >
            Keep it quiet
          </button>
          <button
            className="button"
            onClick={() => {
              onChoose(true);
            }}
          >
            Show me the counts
          </button>
        </div>
      </div>
    </div>
  );
}
